import { Fragment, type ReactNode } from 'react';

/**
 * A deliberately small Markdown renderer.
 *
 * It produces REACT ELEMENTS, never HTML strings, and nothing in this file
 * touches dangerouslySetInnerHTML. That is the whole point: article bodies are
 * written by administrators and reader stories are written by the public, and
 * an editor who pastes `<script>` should end up with those characters on the
 * page rather than a script on the page. Escaping is not something this has to
 * remember to do — React does it by construction, because text is only ever
 * passed as a child and never as markup.
 *
 * It is also small on purpose. A full CommonMark implementation is a large
 * dependency and a large attack surface for a blog that needs headings, bold,
 * italics, links, lists, quotes, images and code. What is here is what the
 * editor's own toolbar can produce; anything else renders as the literal text
 * the author typed, which is a visible, harmless failure rather than a silent
 * or dangerous one.
 *
 * Supported:
 *   # ## ###   headings
 *   **bold**   *italic*   `code`
 *   [text](url)           links
 *   ![alt](url)           images
 *   - item   1. item      lists
 *   > quote               blockquote
 *   ---                   rule
 */

/** Only these schemes may appear in a link or an image. A javascript: URL in an
 *  href is the one way a Markdown renderer can still execute something. */
function safeUrl(raw: string): string | null {
  const url = raw.trim();
  if (url.startsWith('/') || url.startsWith('#')) return url;       // same site
  if (/^https?:\/\//i.test(url)) return url;
  if (/^mailto:/i.test(url) || /^tel:/i.test(url)) return url;
  return null;                                                       // refused
}

/** Inline: bold, italic, code, links, images. Applied in one pass so a link
 *  label can itself contain bold without the two fighting. */
function inline(text: string, keyPrefix: string): ReactNode[] {
  const out: ReactNode[] = [];
  const pattern = /(!?)\[([^\]]*)\]\(([^)\s]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*|`([^`]+)`/g;
  let last = 0;
  let m: RegExpExecArray | null;
  let i = 0;

  while ((m = pattern.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const key = `${keyPrefix}-${i++}`;

    if (m[3] !== undefined) {
      const url = safeUrl(m[3]);
      const label = m[2] ?? '';
      if (!url) {
        // A refused URL becomes plain text rather than vanishing, so an author
        // can see what happened instead of wondering where their link went.
        out.push(<Fragment key={key}>{m[0]}</Fragment>);
      } else if (m[1] === '!') {
        out.push(<img key={key} src={url} alt={label} loading="lazy" className="rich-img" />);
      } else {
        const external = /^https?:\/\//i.test(url);
        out.push(
          <a key={key} href={url}
            {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}>
            {label}
          </a>,
        );
      }
    } else if (m[4] !== undefined) {
      out.push(<strong key={key}>{m[4]}</strong>);
    } else if (m[5] !== undefined) {
      out.push(<em key={key}>{m[5]}</em>);
    } else if (m[6] !== undefined) {
      out.push(<code key={key}>{m[6]}</code>);
    }
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function RichText({ body }: { body: string }) {
  const lines = body.replace(/\r\n/g, '\n').split('\n');

  /**
   * The shallowest heading the author used becomes h2.
   *
   * The page already supplies the h1 — the article title — so a body heading
   * can never be one. But authors are split about what a section marker is:
   * some write `#`, most write `##` because that is what sits under a title in
   * a README. Fixing the mapping to one convention gives the other a document
   * that jumps from h1 straight to h3, which is exactly the malformed heading
   * structure the brief asks us to avoid.
   *
   * Reading the author's own shallowest level and anchoring it at h2 makes
   * both conventions produce a correct outline, and keeps relative depth
   * intact within the article.
   */
  const levels = lines
    .map(l => /^(#{1,3})\s+\S/.exec(l.trim())?.[1]?.length)
    .filter((n): n is number => n !== undefined);
  const shallowest = levels.length ? Math.min(...levels) : 1;
  const blocks: ReactNode[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;
  let key = 0;

  const flushParagraph = () => {
    if (paragraph.length === 0) return;
    blocks.push(<p key={`p${key++}`}>{inline(paragraph.join(' '), `p${key}`)}</p>);
    paragraph = [];
  };
  const flushList = () => {
    if (!list) return;
    const items = list.items.map((it, n) => <li key={n}>{inline(it, `li${key}-${n}`)}</li>);
    blocks.push(list.ordered ? <ol key={`l${key++}`}>{items}</ol> : <ul key={`l${key++}`}>{items}</ul>);
    list = null;
  };
  const flushAll = () => { flushParagraph(); flushList(); };

  for (const raw of lines) {
    const line = raw.trimEnd();

    if (line.trim() === '') { flushAll(); continue; }

    if (/^---+$/.test(line.trim())) { flushAll(); blocks.push(<hr key={`h${key++}`} />); continue; }

    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      flushAll();
      // Normalised against the author's own shallowest heading, so the outline
      // starts at h2 whichever convention they used. Capped at h4.
      const depth = Math.min(heading[1]!.length - shallowest, 2);
      const content = inline(heading[2]!, `hd${key}`);
      blocks.push(depth === 0 ? <h2 key={`h${key++}`}>{content}</h2>
        : depth === 1 ? <h3 key={`h${key++}`}>{content}</h3>
        : <h4 key={`h${key++}`}>{content}</h4>);
      continue;
    }

    const quote = /^>\s?(.*)$/.exec(line);
    if (quote) {
      flushAll();
      blocks.push(<blockquote key={`q${key++}`}>{inline(quote[1]!, `q${key}`)}</blockquote>);
      continue;
    }

    const bullet = /^[-*]\s+(.*)$/.exec(line);
    const numbered = /^\d+\.\s+(.*)$/.exec(line);
    if (bullet || numbered) {
      flushParagraph();
      const ordered = !!numbered;
      // A change of list type starts a new list rather than mixing them.
      if (list && list.ordered !== ordered) flushList();
      list ??= { ordered, items: [] };
      list.items.push((bullet ?? numbered)![1]!);
      continue;
    }

    flushList();
    paragraph.push(line.trim());
  }
  flushAll();

  return <div className="rich">{blocks}</div>;
}

/** First paragraph as plain text, for a summary an author did not write. */
export function excerpt(body: string, max = 200): string {
  const text = body.replace(/\r\n/g, '\n')
    .split('\n').map(l => l.trim())
    .find(l => l && !/^[#>\-*\d]/.test(l)) ?? '';
  const clean = text.replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*`]/g, '');
  return clean.length > max ? `${clean.slice(0, max - 1).trimEnd()}…` : clean;
}
